import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';
import { FoundersCapIcon } from '../icons/FoundersCapIcon';
import { trackButtonClick } from '../../utils/analytics';
import { signalPricingCtaArrival } from '../../utils/ctaActions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = 'foundersCapBandDismissed';

/**
 * FoundersCapStickyBand — slim sticky strip pinned to the bottom of the
 * viewport that surfaces ONCE the visitor has scrolled past the Pricing
 * section. Re-anchors Founders' Cap scarcity at the moment of highest
 * buying intent.
 *
 * Visibility rules:
 *   • Only shown when CMS toggle `founders_cap.show_sticky_band === true`.
 *   • Only shown when the Pricing section has scrolled out of view (so it
 *     doesn't compete with the slider itself).
 *   • Hidden once the visitor reaches the page footer (so it doesn't sit
 *     on top of the footer content).
 *   • Dismissible — once closed, stays closed for the rest of the session.
 *
 * Count source (CMS-controlled):
 *   • `count_mode === 'manual'` — use the integer in `manual_count`.
 *   • Otherwise — pull `founders_claimed` from the live pricing API and
 *     compute (cap - claimed). Defaults to a sensible cap of 100 if not set.
 */
export function FoundersCapStickyBand({ content }) {
  const cms = content?.pricing?.founders_cap || {};
  const enabled = cms.show_sticky_band === true && cms.enabled !== false;
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [livePricing, setLivePricing] = useState(null);
  const fetchedRef = useRef(false);

  // Restore dismissed flag from session so it stays gone after a refresh.
  useEffect(() => {
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === '1') setDismissed(true);
    } catch { /* private-mode safe */ }
  }, []);

  // Pull the live cap count once, only if we're actually going to use it.
  useEffect(() => {
    if (!enabled || cms.count_mode === 'manual' || fetchedRef.current) return;
    fetchedRef.current = true;
    axios.get(`${API}/pricing`).then(r => setLivePricing(r.data)).catch(() => {});
  }, [enabled, cms.count_mode]);

  // Show only AFTER the visitor has scrolled past the Pricing section AND
  // is not yet at the footer. Throttled to one rAF per scroll.
  useEffect(() => {
    if (!enabled || dismissed) { setVisible(false); return undefined; }
    const check = () => {
      const pricing = document.getElementById('pricing');
      const footer  = document.querySelector('footer') || document.getElementById('footer');
      if (!pricing) { setVisible(false); return; }
      const pricingRect = pricing.getBoundingClientRect();
      const beyondPricing = pricingRect.bottom < 100; // pricing has scrolled mostly off-screen
      let beforeFooter = true;
      if (footer) {
        const footerRect = footer.getBoundingClientRect();
        // Hide once the footer top is within 80px of the viewport bottom so
        // the band doesn't overlap the footer content.
        beforeFooter = footerRect.top > (window.innerHeight - 80);
      }
      setVisible(beyondPricing && beforeFooter);
    };
    check();
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { check(); ticking = false; });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', check);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', check);
    };
  }, [enabled, dismissed]);

  if (!enabled || !visible || dismissed) return null;

  // Resolve the displayed count.
  const cap = Number(cms.cap ?? livePricing?.founders_cap ?? 100);
  const claimed = Number(livePricing?.founders_claimed ?? 0);
  const liveLeft = Math.max(0, cap - claimed);
  const manualLeft = Number(cms.manual_count);
  const left = cms.count_mode === 'manual' && Number.isFinite(manualLeft)
    ? manualLeft
    : liveLeft;

  // Copy template — admin can use `{count}` placeholder anywhere in the line.
  const template = cms.band_copy
    || "🧢 Only {count} Founders' Caps left — lock lifetime access.";
  const copy = template.replace('{count}', String(left));
  const ctaText = cms.band_cta_text || 'Claim mine';

  const handleClick = () => {
    trackButtonClick('founders_band_cta', ctaText);
    const el = document.getElementById('pricing');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // Re-fires the pricing pulse so the slider re-asserts itself when the
    // visitor lands back on it from the band.
    signalPricingCtaArrival('pricing');
  };
  const handleDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* private-mode safe */ }
  };

  return (
    <div
      data-testid="founders-cap-sticky-band"
      className="fixed bottom-0 left-0 right-0 z-40 px-4 pb-4 pointer-events-none"
    >
      <div className="max-w-3xl mx-auto pointer-events-auto">
        <div
          className="flex items-center gap-3 px-4 py-3 rounded-2xl shadow-lg ring-1 ring-black/5"
          style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' }}
        >
          <FoundersCapIcon className="w-7 h-7 shrink-0 text-amber-900" />
          <p className="flex-1 text-sm sm:text-base font-semibold text-amber-950 leading-snug">
            {copy}
          </p>
          <button
            type="button"
            onClick={handleClick}
            className="shrink-0 bg-[#007a33] hover:bg-[#005c26] text-white text-xs sm:text-sm font-bold px-4 py-2 rounded-full transition-all"
            data-testid="founders-cap-sticky-cta"
          >
            {ctaText}
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss"
            className="shrink-0 p-1 text-amber-900/60 hover:text-amber-900 transition-colors"
            data-testid="founders-cap-sticky-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default FoundersCapStickyBand;
